import React, { useMemo, useState } from "react";
import {
  Box,
  VStack,
  Heading,
  Text,
  Button,
  Input,
  Select,
  FormControl,
  FormErrorMessage,
  FormLabel,
  SimpleGrid,
  HStack,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { loadApplyFormData, saveApplyFormData } from "./applyFormStorage";
import { FINANCIAL_INSTITUTION_OPTIONS } from "./bankConfig";
import { saveCustomerProfileStep } from "./applyProfileApi";

export default function ApplyBankDetailsPage() {
  const navigate = useNavigate();

  const initialBank = useMemo(() => {
    const stored = loadApplyFormData();
    return stored.bank && typeof stored.bank === "object" ? stored.bank : {};
  }, []);

  const [values, setValues] = useState({
    financialInstitution: initialBank.financialInstitution || "",
    transitNumber: initialBank.transitNumber || "",
    accountNumber: initialBank.accountNumber || "",
  });

  const [touched, setTouched] = useState({
    financialInstitution: false,
    transitNumber: false,
    accountNumber: false,
  });

  const [focusedField, setFocusedField] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const transitIsValid = /^\d{5}$/.test(values.transitNumber);
  const accountNumberIsValid = /^\d{7,12}$/.test(values.accountNumber);

  const errors = {
    financialInstitution: touched.financialInstitution && !values.financialInstitution ? "Bank or financial institution is required" : "",
    transitNumber:
      touched.transitNumber && !values.transitNumber
        ? "Transit number is required"
        : touched.transitNumber && values.transitNumber && !transitIsValid
        ? "Transit number must be exactly 5 digits"
        : "",
    accountNumber:
      touched.accountNumber && !values.accountNumber
        ? "Account number is required"
        : touched.accountNumber && values.accountNumber && !accountNumberIsValid
        ? "Account number must be 7 to 12 digits"
        : "",
  };

  const hasErrors = Object.values(errors).some(Boolean);

  const handleChange = (field) => (event) => {
    const nextValue = event.target.value;

    if (field === "transitNumber") {
      const digitsOnly = nextValue.replace(/\D/g, "").slice(0, 5);
      setValues((prev) => ({ ...prev, transitNumber: digitsOnly }));
      return;
    }

    if (field === "accountNumber") {
      const digitsOnly = nextValue.replace(/\D/g, "").slice(0, 12);
      setValues((prev) => ({ ...prev, accountNumber: digitsOnly }));
      return;
    }

    setValues((prev) => ({ ...prev, [field]: nextValue }));
  };

  const handleFocus = (field) => () => {
    setFocusedField(field);
  };

  const handleBlur = (field) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    setFocusedField((prev) => (prev === field ? "" : prev));
  };

  const isFloating = (field) => focusedField === field || values[field] !== "";

  const setAllTouched = () => {
    setTouched({
      financialInstitution: true,
      transitNumber: true,
      accountNumber: true,
    });
  };

  const buildBankPayload = () => {
    const payload = {};

    const institution = typeof values.financialInstitution === "string" ? values.financialInstitution.trim() : values.financialInstitution;
    const transit = typeof values.transitNumber === "string" ? values.transitNumber.trim() : values.transitNumber;
    const account = typeof values.accountNumber === "string" ? values.accountNumber.trim() : values.accountNumber;

    if (institution) {
      payload.financialInstitution = institution;
    }

    if (transit) {
      payload.transitNumber = transit;
    }

    if (account) {
      payload.accountNumber = account;
    }

    return payload;
  };

  const persistBank = () => {
    saveApplyFormData({ bank: buildBankPayload() });
  };

  const handleBack = () => {
    persistBank();
    const stored = loadApplyFormData();
    const flow = stored.flow && typeof stored.flow === "object" ? stored.flow : {};
    const bankBackPath = flow.incomeBackPath === "/apply/reapply" ? "/apply/reapply" : "/apply/income-info";
    navigate(bankBackPath);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError("");

    setAllTouched();
    if (!values.financialInstitution || !values.transitNumber || !values.accountNumber || !transitIsValid || !accountNumberIsValid || hasErrors) {
      return;
    }

    try {
      setIsSubmitting(true);
      const bankPayload = buildBankPayload();
      saveApplyFormData({ bank: bankPayload });
      await saveCustomerProfileStep("bank", bankPayload);
      navigate("/apply/instant-bank-verification");
    } catch (error) {
      setSubmitError(error.message || "Unable to save your profile right now. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box width="100%" bg="gray.50" minH="100vh" pt={{ base: 28, md: 32 }} pb={{ base: 12, md: 16 }} px={{ base: 4, md: 8 }}>
      <VStack maxW="760px" mx="auto" spacing={8} align="stretch">
        <Box border="1px solid" borderColor="gray.200" borderRadius="2xl" bg="white" boxShadow="sm" px={{ base: 5, md: 8 }} py={{ base: 7, md: 9 }}>
          <VStack spacing={5} align="stretch">
            <VStack spacing={2} align="stretch" textAlign="center">
              <Heading size="lg">Bank Details</Heading>
              <Text color="gray.600" fontSize="sm">
                Enter the account details where your funds should be deposited.
              </Text>
            </VStack>

            <Box border="1px solid" borderColor="gray.200" borderRadius="xl" bg="gray.50" p={{ base: 4, md: 5 }}>
              <Text color="gray.700" fontSize="sm" fontWeight="600" mb={3}>
                Sample cheque illustration
              </Text>
              <Box border="1px dashed" borderColor="gray.300" borderRadius="lg" bg="white" p={4}>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                  <Box>
                    <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="0.04em">
                      Transit Number
                    </Text>
                    <Text fontSize="lg" fontWeight="700" color="gray.800">
                      12345
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="0.04em">
                      Institution Number
                    </Text>
                    <Text fontSize="lg" fontWeight="700" color="gray.800">
                      001
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="0.04em">
                      Account Number
                    </Text>
                    <Text fontSize="lg" fontWeight="700" color="gray.800">
                      1234567
                    </Text>
                  </Box>
                </SimpleGrid>
                <Text mt={3} fontSize="sm" color="gray.600">
                  On most Canadian cheques, the transit number is the 5-digit branch number.
                </Text>
              </Box>
            </Box>

            <form onSubmit={handleSubmit}>
              <VStack spacing={4} align="stretch">
                <FormControl isRequired isInvalid={Boolean(errors.financialInstitution)} position="relative">
                  <FormLabel
                    position="absolute"
                    left="12px"
                    top={isFloating("financialInstitution") ? "-9px" : "13px"}
                    zIndex={2}
                    px={2}
                    bg="white"
                    fontSize={isFloating("financialInstitution") ? "xs" : "sm"}
                    color={isFloating("financialInstitution") ? "gray.700" : "gray.500"}
                    pointerEvents="none"
                    transition="all 0.2s ease"
                    m={0}
                  >
                    Bank / Financial Institution
                  </FormLabel>
                  <Select
                    value={values.financialInstitution}
                    onChange={handleChange("financialInstitution")}
                    onFocus={handleFocus("financialInstitution")}
                    onBlur={handleBlur("financialInstitution")}
                    bg="white"
                    h="52px"
                    borderRadius="lg"
                    borderColor="gray.300"
                    _focus={{ borderColor: "black", boxShadow: "none" }}
                    _hover={{ borderColor: "gray.400" }}
                    required
                  >
                    <option value="" disabled hidden />
                    {FINANCIAL_INSTITUTION_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>{errors.financialInstitution}</FormErrorMessage>
                </FormControl>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl isRequired isInvalid={Boolean(errors.transitNumber)} position="relative">
                    <FormLabel
                      position="absolute"
                      left="12px"
                      top={isFloating("transitNumber") ? "-9px" : "13px"}
                      zIndex={2}
                      px={2}
                      bg="white"
                      fontSize={isFloating("transitNumber") ? "xs" : "sm"}
                      color={isFloating("transitNumber") ? "gray.700" : "gray.500"}
                      pointerEvents="none"
                      transition="all 0.2s ease"
                      m={0}
                    >
                      Transit Number
                    </FormLabel>
                    <Input
                      value={values.transitNumber}
                      onChange={handleChange("transitNumber")}
                      onFocus={handleFocus("transitNumber")}
                      onBlur={handleBlur("transitNumber")}
                      inputMode="numeric"
                      maxLength={5}
                      bg="white"
                      h="52px"
                      borderRadius="lg"
                      borderColor="gray.300"
                      _focus={{ borderColor: "black", boxShadow: "none" }}
                      _hover={{ borderColor: "gray.400" }}
                      placeholder=""
                      required
                    />
                    <FormErrorMessage>{errors.transitNumber}</FormErrorMessage>
                  </FormControl>

                  <FormControl isRequired isInvalid={Boolean(errors.accountNumber)} position="relative">
                    <FormLabel
                      position="absolute"
                      left="12px"
                      top={isFloating("accountNumber") ? "-9px" : "13px"}
                      zIndex={2}
                      px={2}
                      bg="white"
                      fontSize={isFloating("accountNumber") ? "xs" : "sm"}
                      color={isFloating("accountNumber") ? "gray.700" : "gray.500"}
                      pointerEvents="none"
                      transition="all 0.2s ease"
                      m={0}
                    >
                      Account Number
                    </FormLabel>
                    <Input
                      value={values.accountNumber}
                      onChange={handleChange("accountNumber")}
                      onFocus={handleFocus("accountNumber")}
                      onBlur={handleBlur("accountNumber")}
                      inputMode="numeric"
                      maxLength={12}
                      bg="white"
                      h="52px"
                      borderRadius="lg"
                      borderColor="gray.300"
                      _focus={{ borderColor: "black", boxShadow: "none" }}
                      _hover={{ borderColor: "gray.400" }}
                      placeholder=""
                      required
                    />
                    <FormErrorMessage>{errors.accountNumber}</FormErrorMessage>
                  </FormControl>
                </SimpleGrid>

                <HStack spacing={3} justify="space-between">
                  <Button type="button" variant="outline" borderColor="gray.300" color="gray.700" borderRadius="full" h="50px" px={8} _hover={{ bg: "gray.50" }} onClick={handleBack}>
                    Back
                  </Button>
                  <Button type="submit" isLoading={isSubmitting} bg="black" color="white" borderRadius="full" h="50px" px={8} _hover={{ bg: "gray.900" }}>
                    Continue
                  </Button>
                </HStack>

                {submitError && (
                  <Text color="red.500" fontSize="sm">
                    {submitError}
                  </Text>
                )}
              </VStack>
            </form>
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
}